import { Head, usePage } from "@inertiajs/react"
import { useTranslation } from "react-i18next"

import HeadingSmall from "@/components/heading-small"
import TiktokAccountCard, {
  type TiktokAccount,
} from "@/components/social-accounts/tiktok-account-card"
import { Alert, AlertDescription } from "@/components/ui/alert"
import AppLayout from "@/layouts/app-layout"
import SettingsLayout from "@/layouts/settings/layout"
import { settingsSocialAccountsPath } from "@/routes"
import type { BreadcrumbItem, FlashData } from "@/types"
import type { FailureReason } from "@/types/social-accounts"

export interface SettingsSocialAccountsShowProps {
  /** BE-locked shape per BE TRD §"SettingsSocialAccountsShowProps". */
  tiktok_link: TiktokAccount | null
  /** Server-resolved POST URL — `tiktokReconnectPath()`. */
  reconnect_url: string
  /** Server-resolved per-brand support email (with OKS fallback). */
  support_email: string
}

/**
 * Settings → Social Accounts page.
 *
 * Per UX Spec §1.D + v2 amendments:
 *   - Single `<TiktokAccountCard>` rendering active / refresh_needed
 *     (UI-collapsed to active) / revoked / null (defensive).
 *   - **Inline error Alert ABOVE the card** when the previous OAuth
 *     re-link failed — driven by `flash.tiktok_failure_reason` from
 *     `usePage().props.flash`. Five testid variants:
 *     `settings-social-accounts-tiktok-error-{oauth_denied|identity_mismatch|already_linked|network_error|state_expired}`.
 *   - sonner success toast on `flash.success` (per OKS-22 OS pattern).
 *
 * **Flash key sourcing** (per OKS-22 lessons):
 *   - `inertia_rails` 3.21+ requires `:tiktok_failure_reason` in
 *     `config.flash_keys` — BE adds this. FE reads from
 *     `usePage().props.flash` (note: NOT page-level `errors` because
 *     this is a session flash, not a validation-fail re-render).
 *   - We read flash, dispatch the toast inside `useEffect`, and render
 *     the inline Alert based on `failure_reason`. This stays ONLY
 *     within the page-component lifecycle — no global flash machinery
 *     hijack.
 */
export default function SettingsSocialAccountsShow({
  tiktok_link,
  reconnect_url,
  support_email,
}: SettingsSocialAccountsShowProps) {
  const { t } = useTranslation()
  // Inertia flash payload. `useFlash` (mounted by `PersistentLayout`)
  // already toasts `success` / `error` keys at the layout level — we
  // only need to consume the OKS-23-additive `tiktok_failure_reason`
  // here to render the inline Alert above the card.
  const flash = usePage().props.flash as FlashData | undefined
  const failureReason: FailureReason | null =
    flash?.tiktok_failure_reason ?? null

  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: t("settings.social_accounts.breadcrumb"),
      href: settingsSocialAccountsPath(),
    },
  ]

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={t("settings.social_accounts.head_title")} />

      <SettingsLayout>
        <div className="space-y-6" data-testid="settings-social-accounts">
          <HeadingSmall
            title={t("settings.social_accounts.title")}
            description={t("settings.social_accounts.description")}
          />

          {failureReason && (
            <Alert
              variant="destructive"
              data-testid={`settings-social-accounts-tiktok-error-${failureReason.replace(/_/g, "-")}`}
            >
              <AlertDescription>
                {t(`tiktok.relink_error.${failureReason}.body`, {
                  support_email,
                })}
              </AlertDescription>
            </Alert>
          )}

          <TiktokAccountCard
            tiktok_link={tiktok_link}
            reconnect_url={reconnect_url}
          />
        </div>
      </SettingsLayout>
    </AppLayout>
  )
}
